import { useEffect, useState } from "react";
import { KeyRound, MessageSquareText, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full rounded-xl border border-white/10 bg-noir px-4 py-3 text-sm text-white outline-none transition-colors focus:border-neon/60";

export const PasswordForm = ({ token, onLogout, onToken }) => {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [msg, setMsg] = useState(null);
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setMsg(null);
        if (next !== confirm) return setMsg({ ok: false, text: "Les deux nouveaux mots de passe ne correspondent pas." });
        setSaving(true);
        try {
            const res = await fetch(`${API}/admin/password`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ current_password: current, new_password: next }),
            });
            if (res.status === 401) return onLogout();
            const data = await res.json();
            if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Erreur");
            setCurrent(""); setNext(""); setConfirm("");
            if (data.token) onToken(data.token);
            setMsg({ ok: true, text: "Mot de passe modifié. Utilisez-le à votre prochaine connexion." });
        } catch (err) {
            setMsg({ ok: false, text: err.message });
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="card-line rounded-3xl bg-panel p-5 sm:p-6" data-testid="admin-password-form">
            <h2 className="flex items-center gap-2 font-display text-base font-bold text-white">
                <KeyRound size={16} className="text-neon" /> Changer le mot de passe
            </h2>
            <p className="mt-1 text-xs text-slate-500">8 caractères minimum. Vous seul le connaîtrez.</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <input type="password" data-testid="pwd-current-input" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Mot de passe actuel" className={inputCls} autoComplete="current-password" />
                <input type="password" data-testid="pwd-new-input" value={next} onChange={(e) => setNext(e.target.value)} placeholder="Nouveau mot de passe" className={inputCls} autoComplete="new-password" />
                <input type="password" data-testid="pwd-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirmer" className={inputCls} autoComplete="new-password" />
            </div>
            {msg && (
                <p data-testid="pwd-feedback" className={`mt-3 text-sm ${msg.ok ? "text-emerald-300" : "text-red-300"}`}>{msg.text}</p>
            )}
            <button
                type="submit"
                data-testid="pwd-submit-button"
                disabled={saving || !current || next.length < 8 || !confirm}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-neon px-5 py-2.5 text-sm font-bold text-noir transition-shadow duration-300 hover:shadow-[0_0_26px_rgba(0,240,255,0.45)] disabled:opacity-50"
            >
                <Save size={14} /> {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
        </form>
    );
};

export const MessagesForm = ({ token, onLogout }) => {
    const [values, setValues] = useState({ message_confirmation: "", message_annulation: "" });
    const [msg, setMsg] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetch(`${API}/admin/messages`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => d && setValues(d))
            .catch(() => {});
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setMsg(null);
        setSaving(true);
        try {
            const res = await fetch(`${API}/admin/messages`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify(values),
            });
            if (res.status === 401) return onLogout();
            if (!res.ok) throw new Error("Enregistrement impossible.");
            setValues(await res.json());
            setMsg({ ok: true, text: "Messages enregistrés." });
        } catch (err) {
            setMsg({ ok: false, text: err.message });
        } finally {
            setSaving(false);
        }
    };

    const field = (key, label, testid) => (
        <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
            <textarea
                data-testid={testid}
                value={values[key]}
                onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                rows={3}
                maxLength={600}
                className={`${inputCls} mt-1.5 resize-y`}
            />
        </label>
    );

    return (
        <form onSubmit={submit} className="card-line rounded-3xl bg-panel p-5 sm:p-6" data-testid="admin-messages-form">
            <h2 className="flex items-center gap-2 font-display text-base font-bold text-white">
                <MessageSquareText size={16} className="text-neon" /> Messages envoyés au client
            </h2>
            <p className="mt-1 text-xs text-slate-500">
                Ajouté à l'e-mail automatique reçu par le client quand vous confirmez ou annulez sa réservation.
            </p>
            <div className="mt-4 space-y-4">
                {field("message_confirmation", "Message de confirmation", "msg-confirmation-input")}
                {field("message_annulation", "Message d'annulation", "msg-annulation-input")}
            </div>
            {msg && (
                <p data-testid="msg-feedback" className={`mt-3 text-sm ${msg.ok ? "text-emerald-300" : "text-red-300"}`}>{msg.text}</p>
            )}
            <button
                type="submit"
                data-testid="msg-submit-button"
                disabled={saving}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-neon px-5 py-2.5 text-sm font-bold text-noir transition-shadow duration-300 hover:shadow-[0_0_26px_rgba(0,240,255,0.45)] disabled:opacity-50"
            >
                <Save size={14} /> {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
        </form>
    );
};
